import { useTranslation } from "react-i18next";
import { Paperclip, Trash2, X } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { cn } from "../../../lib/utils";
import { MODE_LABEL_KEY, isOwnVehicleMode } from "../constants/conveyanceConstants";
import {
  TRIP_REASON,
  TRIP_REASON_LABEL_KEY,
  abbreviationFor,
  formatAmount,
} from "../constants/claimConstants";
import {
  computeLineAmount,
  computeLineTotal,
  proofRequiredForLine,
  rateForMode,
  tollsParkingVisible,
  toIsoDate,
} from "../lib/claimRules";
import {
  AmountField,
  Field,
  ReadOnlyValue,
  TextInput,
  borderFor,
  selectBaseClass,
} from "./formControls";

/**
 * One dated trip line on a Rate-per-km claim.
 *
 * Every piece of conditional behaviour on this row is read from the active
 * policy rather than decided here:
 *
 *   - the Mode dropdown lists only `modeOptions`, i.e. the modes the policy has
 *     enabled; a mode HR switched off never appears, even on an older line
 *   - Rate is pulled from the policy's row for the chosen mode and is disabled
 *     under every mode, own-vehicle included
 *   - Amount is auto (distance x rate) for own-vehicle modes and a manual
 *     at-actuals entry for the rest
 *   - Parking / Toll are removed from the row entirely unless the mode is
 *     own-vehicle AND the policy covers them
 *   - Proofs flips to mandatory from the same rule the validator uses
 *
 * Customer and Area come from CRM. Area is a location dropdown filtered to the
 * chosen customer on a site visit, and plain free text for any other reason.
 */
export function TripLineCard({
  line,
  index,
  modes,
  modeOptions,
  policy,
  bounds,
  currencies,
  metricId,
  customers,
  customersLoading,
  onCustomersNeeded,
  locations,
  locationsLoading,
  onLocationsNeeded,
  errors = {},
  disabled = false,
  canRemove = true,
  onChange,
  onRemove,
}) {
  const { t } = useTranslation();

  const ownVehicle = isOwnVehicleMode(line.mode);
  const rate = rateForMode(modes, line.mode);
  const showTollsParking = tollsParkingVisible(line, policy);
  const isSiteVisit = line.reason === TRIP_REASON.CUSTOMER_SITE_VISIT;
  const amount = computeLineAmount(line, modes);
  const total = computeLineTotal(line, modes, policy);
  const proofNeeded = proofRequiredForLine(line, policy);
  const abbreviation = abbreviationFor(currencies, metricId);

  const set = (patch) => onChange({ ...line, ...patch });

  /**
   * Switching Reason clears whatever no longer applies, and switching Mode
   * re-evaluates the amount: a manual at-actuals figure must not survive a move
   * to an auto-calculated mode, and parking/toll must not survive a move to a
   * mode that hides them.
   */
  const handleReasonChange = (reason) => {
    const nextIsSiteVisit = reason === TRIP_REASON.CUSTOMER_SITE_VISIT;
    set({
      reason,
      customerId: nextIsSiteVisit ? line.customerId : "",
      areaLocationId: nextIsSiteVisit ? line.areaLocationId : "",
      description: nextIsSiteVisit ? "" : line.description,
    });
  };

  const handleModeChange = (mode) => {
    const nextOwnVehicle = isOwnVehicleMode(mode);
    set({
      mode,
      amount: nextOwnVehicle ? "" : line.amount,
      distanceKm: nextOwnVehicle ? line.distanceKm : "",
      parkingCharges: nextOwnVehicle ? line.parkingCharges : "",
      tollCharges: nextOwnVehicle ? line.tollCharges : "",
    });
  };

  const handleCustomerChange = (customerId) => {
    // Changing customer resets Area — the location list is filtered to it.
    set({ customerId, areaLocationId: "" });
  };

  const handleProofsAdded = (event) => {
    const picked = Array.from(event.target.files || []);
    if (picked.length) set({ proofs: [...(line.proofs || []), ...picked] });
    event.target.value = "";
  };

  const removeProof = (proofIndex) => {
    set({ proofs: (line.proofs || []).filter((_, i) => i !== proofIndex) });
  };

  return (
    <div className="rounded-2xl border border-[var(--color-text-border)] bg-white p-4 sm:p-5">
      <div className="flex items-center justify-between gap-3 mb-4">
        <p className="text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
          {t("conveyanceClaim.tripLineNo", { number: index + 1 })}
        </p>
        {canRemove ? (
          <button
            type="button"
            onClick={onRemove}
            disabled={disabled}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-[var(--color-text-border)] text-[11px] font-bold text-[var(--color-text-secondary)] hover:text-[var(--color-error)] hover:border-[var(--color-error)]/40 transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Trash2 className="h-3.5 w-3.5" />
            {t("conveyanceClaim.removeTrip")}
          </button>
        ) : null}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Field label={t("conveyanceClaim.tripDate")} required error={errors.tripDate}>
          <TextInput
            type="date"
            value={line.tripDate || ""}
            disabled={disabled}
            min={bounds ? toIsoDate(bounds.min) : undefined}
            max={bounds ? toIsoDate(bounds.max) : undefined}
            error={errors.tripDate}
            onChange={(event) => set({ tripDate: event.target.value })}
          />
        </Field>

        <Field label={t("conveyanceClaim.reason")} required error={errors.reason}>
          <Select
            value={line.reason || undefined}
            disabled={disabled}
            onValueChange={handleReasonChange}
          >
            <SelectTrigger className={cn(selectBaseClass, borderFor(errors.reason))}>
              <SelectValue placeholder={t("conveyanceClaim.selectReason")} />
            </SelectTrigger>
            <SelectContent>
              {Object.values(TRIP_REASON).map((reason) => (
                <SelectItem key={reason} value={reason}>
                  {t(TRIP_REASON_LABEL_KEY[reason])}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        {/* Customer is hidden entirely — not disabled — for the other reasons. */}
        {isSiteVisit ? (
          <Field label={t("conveyanceClaim.customer")} required error={errors.customerId}>
            <Select
              value={line.customerId ? String(line.customerId) : undefined}
              disabled={disabled}
              onOpenChange={(open) => open && onCustomersNeeded()}
              onValueChange={handleCustomerChange}
            >
              <SelectTrigger className={cn(selectBaseClass, borderFor(errors.customerId))}>
                <SelectValue
                  placeholder={
                    customersLoading
                      ? t("common.loading")
                      : customers.length
                        ? t("conveyanceClaim.selectCustomer")
                        : t("conveyanceClaim.noCustomers")
                  }
                />
              </SelectTrigger>
              <SelectContent className="max-h-60 overflow-y-auto">
                {customers.map((customer) => (
                  <SelectItem key={customer.id} value={String(customer.id)}>
                    {customer.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        ) : line.reason ? (
          <Field
            label={t("conveyanceClaim.description")}
            required
            error={errors.description}
            className="sm:col-span-2 lg:col-span-1"
          >
            <TextInput
              type="text"
              maxLength={500}
              value={line.description || ""}
              disabled={disabled}
              placeholder={t("conveyanceClaim.descriptionPlaceholder")}
              error={errors.description}
              onChange={(event) => set({ description: event.target.value })}
            />
          </Field>
        ) : null}

        <Field label={t("common.from")} required error={errors.fromLocation}>
          <TextInput
            type="text"
            maxLength={255}
            value={line.fromLocation || ""}
            disabled={disabled}
            placeholder={t("conveyanceClaim.fromPlaceholder")}
            error={errors.fromLocation}
            onChange={(event) => set({ fromLocation: event.target.value })}
          />
        </Field>

        <Field label={t("common.to")} required error={errors.toLocation}>
          <TextInput
            type="text"
            maxLength={255}
            value={line.toLocation || ""}
            disabled={disabled}
            placeholder={t("conveyanceClaim.toPlaceholder")}
            error={errors.toLocation}
            onChange={(event) => set({ toLocation: event.target.value })}
          />
        </Field>

        {/* Area is a CRM location dropdown, shown only for a customer site visit. */}
        {isSiteVisit ? (
          <Field
            label={t("conveyanceClaim.area")}
            required
            error={errors.areaLocationId}
            hint={!line.customerId ? t("conveyanceClaim.areaNeedsCustomer") : undefined}
          >
            <Select
              value={line.areaLocationId ? String(line.areaLocationId) : undefined}
              disabled={disabled || !line.customerId}
              onOpenChange={(open) => open && onLocationsNeeded(line.customerId)}
              onValueChange={(value) => set({ areaLocationId: value })}
            >
              <SelectTrigger className={cn(selectBaseClass, borderFor(errors.areaLocationId))}>
                <SelectValue
                  placeholder={
                    locationsLoading
                      ? t("common.loading")
                      : locations.length
                        ? t("conveyanceClaim.selectArea")
                        : t("conveyanceClaim.noAreas")
                  }
                />
              </SelectTrigger>
              <SelectContent className="max-h-60 overflow-y-auto">
                {locations.map((location) => (
                  <SelectItem key={location.id} value={String(location.id)}>
                    {location.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        ) : null}

        <Field label={t("conveyanceClaim.modeOfTransport")} required error={errors.mode}>
          <Select value={line.mode || undefined} disabled={disabled} onValueChange={handleModeChange}>
            <SelectTrigger className={cn(selectBaseClass, borderFor(errors.mode))}>
              <SelectValue
                placeholder={
                  modeOptions.length
                    ? t("conveyanceClaim.selectMode")
                    : t("conveyanceClaim.noModesEnabled")
                }
              />
            </SelectTrigger>
            <SelectContent>
              {modeOptions.map((mode) => (
                <SelectItem key={mode} value={mode}>
                  {t(MODE_LABEL_KEY[mode] || mode)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field
          label={t("conveyanceClaim.distanceKm")}
          required={ownVehicle}
          error={errors.distanceKm}
        >
          <TextInput
            type="number"
            step="0.1"
            min="0"
            inputMode="decimal"
            value={line.distanceKm ?? ""}
            disabled={disabled || !ownVehicle}
            error={errors.distanceKm}
            onChange={(event) => set({ distanceKm: event.target.value })}
          />
        </Field>

        {/* Never editable under any mode — the policy owns it. */}
        <Field label={t("localConveyance.ratePerKm")}>
          <ReadOnlyValue>
            {ownVehicle && rate !== null
              ? formatAmount(rate, abbreviation)
              : t("conveyanceClaim.rateNotApplicable")}
          </ReadOnlyValue>
        </Field>

        <Field
          label={t("conveyanceClaim.amount")}
          required={!ownVehicle}
          error={errors.amount}
          hint={ownVehicle ? t("conveyanceClaim.amountAutoHint") : t("localConveyance.atActuals")}
        >
          <AmountField
            value={ownVehicle ? amount : line.amount}
            onValueChange={(next) => set({ amount: next })}
            abbreviation={abbreviation}
            disabled={disabled}
            readOnly={ownVehicle}
            error={errors.amount}
          />
        </Field>

        {showTollsParking ? (
          <>
            <Field label={t("conveyanceClaim.parkingCharges")} error={errors.parkingCharges}>
              <AmountField
                value={line.parkingCharges}
                onValueChange={(next) => set({ parkingCharges: next })}
                abbreviation={abbreviation}
                disabled={disabled}
                error={errors.parkingCharges}
              />
            </Field>
            <Field label={t("conveyanceClaim.tollCharges")} error={errors.tollCharges}>
              <AmountField
                value={line.tollCharges}
                onValueChange={(next) => set({ tollCharges: next })}
                abbreviation={abbreviation}
                disabled={disabled}
                error={errors.tollCharges}
              />
            </Field>
          </>
        ) : null}
      </div>

      {/* Proofs */}
      <div className="mt-4">
        <Field
          label={t("conveyanceClaim.proofs")}
          required={proofNeeded}
          error={errors.proofs}
          hint={proofNeeded ? t("conveyanceClaim.proofRequiredHint") : t("conveyanceClaim.proofOptionalHint")}
        >
          <div className="flex flex-wrap items-center gap-2">
            <label
              className={cn(
                "inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-[var(--color-text-border)] bg-white text-sm font-semibold text-foreground hover:bg-gray-50 transition",
                disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
              )}
            >
              <Paperclip className="h-4 w-4" />
              {t("conveyanceClaim.attachProof")}
              <input
                type="file"
                multiple
                accept="image/*,application/pdf"
                disabled={disabled}
                onChange={handleProofsAdded}
                className="sr-only"
              />
            </label>

            {(line.proofs || []).map((proof, proofIndex) => (
              <span
                key={`${proof.name}-${proofIndex}`}
                className="inline-flex items-center gap-1.5 h-8 pl-3 pr-1.5 rounded-lg bg-[var(--color-workspace)]/50 text-[11px] font-semibold text-foreground max-w-[220px]"
              >
                <span className="truncate">{proof.name}</span>
                <button
                  type="button"
                  onClick={() => removeProof(proofIndex)}
                  disabled={disabled}
                  aria-label={t("formActions.remove")}
                  className="h-5 w-5 grid place-items-center rounded text-[var(--color-text-secondary)] hover:text-[var(--color-error)] transition cursor-pointer disabled:cursor-not-allowed"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
        </Field>
      </div>

      <div className="mt-4 pt-3 border-t border-[var(--color-text-border)]/60 flex items-center justify-between gap-3">
        <span className="text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
          {t("conveyanceClaim.lineTotal")}
        </span>
        <span className="text-base font-extrabold text-foreground">
          {formatAmount(total, abbreviation)}
        </span>
      </div>
    </div>
  );
}

export default TripLineCard;
